import React, { FC, useState } from "react";
import { HiOutlineStar } from "react-icons/hi2";
import {
  LuArchive,
  LuBan,
  LuChevronDown,
  LuFileText,
  LuFolder,
  LuInbox,
  LuSend,
  LuTrash2,
} from "react-icons/lu";

import { FolderKind, MailFolder } from "./mailPage.utils";

const ICONS: Record<FolderKind, React.ReactNode> = {
  inbox: <LuInbox size={18} />,
  starred: <HiOutlineStar size={18} />,
  sent: <LuSend size={17} />,
  drafts: <LuFileText size={17} />,
  spam: <LuBan size={17} />,
  archive: <LuArchive size={17} />,
  trash: <LuTrash2 size={17} />,
  other: <LuFolder size={17} />,
};

type Props = {
  folders: MailFolder[];
  moreFolders: MailFolder[];
  current: MailFolder | null;
  onPick: (folder: MailFolder) => void;
  /** Mailbox not connected / not reachable: names shown greyed out. */
  disabled?: boolean;
};

/**
 * Left column: the mailbox folders like Outlook (Inbox, Starred, Sent, Drafts,
 * Spam, Archive, Deleted, then "More folders"). No AI "Smart views" (owner,
 * Roadmap 1840).
 */
export const MailFolderRail: FC<Props> = ({
  folders,
  moreFolders,
  current,
  onPick,
  disabled = false,
}) => {
  const [showMore, setShowMore] = useState(false);

  const row = (f: MailFolder) => {
    const on = !disabled && current?.kind === f.kind && current?.id === f.id;
    const count =
      f.kind === "inbox" ? f.unread : f.kind === "drafts" ? f.total : 0;
    return (
      <button
        type={"button"}
        key={`${f.kind}-${f.id}-${f.label}`}
        disabled={disabled}
        title={
          disabled ? "Connect your mailbox to open this folder" : undefined
        }
        onClick={() => onPick(f)}
        className={
          "flex h-[38px] w-full flex-none items-center gap-[11px] rounded-[9px] pl-3 pr-[10px] text-left text-[14px] enabled:hover:bg-[#EEEAFF] enabled:hover:text-[#6B4EFF] disabled:cursor-not-allowed max-md:h-[34px] max-md:w-auto max-md:gap-2 max-md:whitespace-nowrap max-md:pr-3 max-md:text-[13px]"
        }
        style={{
          background: on ? "#EEEAFF" : "transparent",
          color: on ? "#6B4EFF" : disabled ? "#A3A9B8" : "#3A4256",
          fontWeight: on ? 700 : 600,
        }}>
        <span className={"flex-none"}>{ICONS[f.kind]}</span>
        <span className={"min-w-0 flex-1 truncate"}>{f.label}</span>
        {count > 0 && (
          <span
            className={
              "inline-flex h-5 min-w-[22px] items-center justify-center rounded-[10px] px-[7px] text-[11.5px] font-bold"
            }
            style={{
              background: on ? "#6B4EFF" : "#E6E8EE",
              color: on ? "#fff" : "#5B6678",
            }}>
            {count}
          </span>
        )}
      </button>
    );
  };

  return (
    <nav
      aria-label={"Mail folders"}
      // Phones (QA PHONE-012): one scrollable row of folders above the mail, not a 196 px column.
      className={
        "flex w-[196px] flex-none flex-col gap-[2px] overflow-y-auto max-md:w-full max-md:flex-row max-md:gap-1 max-md:overflow-x-auto max-md:overflow-y-hidden max-md:pb-1"
      }>
      {folders.map(row)}
      {moreFolders.length > 0 && (
        <>
          <button
            type={"button"}
            onClick={() => setShowMore((v) => !v)}
            className={
              "flex h-[34px] items-center gap-2 rounded-[9px] pl-3 text-[13px] font-semibold text-[#8A90A2] hover:text-[#6B4EFF]"
            }>
            <LuChevronDown
              size={14}
              style={{
                transform: showMore ? "rotate(0deg)" : "rotate(-90deg)",
                transition: "transform .15s",
              }}
            />
            {showMore
              ? "Fewer folders"
              : `More folders (${moreFolders.length})`}
          </button>
          {showMore && moreFolders.map(row)}
        </>
      )}

      {disabled && (
        <div
          className={
            "mx-1 mt-[14px] rounded-[10px] border border-dashed border-[#DCE2EA] bg-white px-3 py-[11px] text-[12px] leading-[1.5] text-[#6B7185] max-md:hidden"
          }>
          Your folders appear here once your mailbox is connected.
        </div>
      )}
    </nav>
  );
};
